import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { APP_BUILD } from "@/buildInfo";
import { ShieldCheck, LayoutDashboard, FilePlus2, Briefcase, Users, UserCog, Bell, LogOut, ScrollText, Menu, Receipt, Wallet, FolderLock, FileQuestion, ClipboardCheck, Palette, DollarSign, Building2, Inbox, Stamp, UserCircle, MonitorPlay } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { DemoTour } from "@/components/DemoTour";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV = {
  client: [
    { to: "/client", label: "Dashboard", icon: LayoutDashboard },
    { to: "/client/onboarding", label: "Onboarding", icon: ClipboardCheck },
    { to: "/client/start", label: "New Service Order", icon: FilePlus2 },
    { to: "/client/history", label: "Service History", icon: ScrollText },
    { to: "/client/invoices", label: "Billing", icon: Receipt },
    { to: "/profile", label: "Profile", icon: UserCircle },
  ],
  server: [
    { to: "/server", label: "Assignments", icon: Briefcase },
    { to: "/server/onboarding", label: "Onboarding", icon: ClipboardCheck },
    { to: "/server/history", label: "Job History", icon: ScrollText },
    { to: "/server/payouts", label: "Payouts", icon: Wallet },
    { to: "/profile", label: "Profile", icon: UserCircle },
  ],
  admin: [
    { to: "/admin", label: "Command Center", icon: LayoutDashboard },
    { to: "/admin/jobs", label: "All Jobs", icon: Briefcase },
    { to: "/admin/clients", label: "Clients", icon: Users },
    { to: "/admin/servers", label: "Providers", icon: UserCog },
    { to: "/admin/billing", label: "Billing", icon: Receipt },
    { to: "/admin/pricing", label: "Pricing", icon: DollarSign },
    { to: "/admin/payouts", label: "Payouts", icon: Wallet },
    { to: "/admin/verify-report", label: "Verify Report", icon: ShieldCheck },
    { to: "/admin/documents", label: "Documents", icon: FolderLock },
    { to: "/admin/requests", label: "Requests", icon: FileQuestion },
    { to: "/admin/branding", label: "Branding", icon: Palette },
    { to: "/admin/agencies", label: "Agencies", icon: Building2 },
    { to: "/admin/agency-onboarding", label: "Agency Onboarding", icon: Inbox },
    { to: "/admin/intake", label: "Intake", icon: Inbox },
    { to: "/admin/notary", label: "Notary", icon: Stamp },
    { to: "/admin/demo", label: "Demo Environments", icon: MonitorPlay },
    { to: "/profile", label: "Profile", icon: UserCircle },
  ],
  agency: [
    { to: "/agency", label: "Dashboard", icon: LayoutDashboard },
  ],
};

const ROLE_LABEL = {
  client: "Attorney / Client Portal",
  server: "Process Server Portal",
  admin: "Audit Ready Admin",
  agency: "Agency Portal",
};

function NavContent({ items, onNavigate }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [intake, setIntake] = useState(0);
  const hasIntake = items.some((it) => it.to === "/admin/intake");
  useEffect(() => {
    if (!hasIntake) return undefined;
    const load = () => api.get("/admin/intake-queue").then(({ data }) => setIntake(data.total)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    window.addEventListener("focus", load);
    window.addEventListener("intake-refresh", load);
    return () => { clearInterval(iv); window.removeEventListener("focus", load); window.removeEventListener("intake-refresh", load); };
  }, [hasIntake]);
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const active = loc.pathname === it.to;
        const Icon = it.icon;
        return (
          <button
            key={it.to}
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => { nav(it.to); onNavigate?.(); }}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
              active ? "bg-emerald-500 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" />
            {it.label}
            {it.to === "/admin/intake" && intake > 0 && (
              <span data-testid="intake-badge" className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">{intake}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

function NotificationsBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const jobPath = (orderId) => {
    if (!orderId) return null;
    if (user.role === "server") return `/server/jobs/${orderId}`;
    if (user.role === "admin") return `/admin/jobs/${orderId}`;
    return `/client/orders/${orderId}`;
  };
  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  const unread = notifs.filter((n) => !n.read).length;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) api.post("/notifications/read").then(load); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50">
          <Bell className="h-4.5 w-4.5 h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b border-slate-100 px-4 py-3">
          <p className="font-display text-sm font-bold text-slate-900">Notifications</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifs.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-400">No notifications yet</p>}
          {notifs.map((n) => {
            const path = jobPath(n.order_id);
            return (
              <button key={n.id} data-testid={`notification-${n.id}`} disabled={!path}
                onClick={() => { if (path) { setOpen(false); nav(path); } }}
                className={`block w-full border-b border-slate-50 px-4 py-3 text-left last:border-0 ${path ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}>
                <p className="text-sm text-slate-700">{n.message}</p>
                <p className="mt-1 font-mono-arps text-[11px] text-slate-400">
                  {n.job_number ? `#${n.job_number} · ` : ""}{new Date(n.created_at).toLocaleString()}
                </p>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  let items = NAV[user.role] || [];
  // Notary-capable providers get a dedicated Notary Jobs entry (reuses the provider portal shell).
  if (user.role === "server" && ["notary", "both"].includes(user.service_capability)) {
    items = [items[0], { to: "/server/notary", label: "Notary Jobs", icon: Stamp }, ...items.slice(1)];
  }

  const Brand = (
    <div className="flex items-center gap-2.5 px-1 py-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white p-1">
        <BrandLogo className="h-full w-auto" />
      </div>
      <div>
        <p className="font-display text-sm font-extrabold leading-tight tracking-tight text-white">Audit Ready<br />Process Service</p>
      </div>
    </div>
  );

  const Sidebar = (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {Brand}
        <div className="mb-3 mt-2 rounded-lg bg-slate-800/60 px-3 py-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-400">{ROLE_LABEL[user.role]}</p>
          {user.agency_name && <p data-testid="sidebar-business-name" className="mt-0.5 truncate text-sm font-semibold text-white">{user.agency_name}</p>}
        </div>
        <NavContent items={items} />
      </div>
      <div className="shrink-0 border-t border-slate-800 pt-3">
        <div className="mb-2 px-2">
          <p className="truncate text-sm font-semibold text-white">{user.name}</p>
          <p className="truncate text-xs text-slate-400">{user.email}</p>
        </div>
        <button data-testid="logout-button" onClick={logout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 transition hover:bg-red-500/10 hover:text-red-400">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden w-64 shrink-0 bg-slate-900 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col">{Sidebar}</aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-xl lg:px-8">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="rounded-lg border border-slate-200 p-2 lg:hidden">
                  <Menu className="h-5 w-5 text-slate-700" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 border-0 bg-slate-900 p-0">
                {Sidebar}
              </SheetContent>
            </Sheet>
            <h1 className="font-display text-lg font-bold tracking-tight text-slate-900 lg:text-xl">{ROLE_LABEL[user.role]}</h1>
            {user.agency_name && <span data-testid="header-business-name" className="hidden rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 sm:inline">{user.agency_name}</span>}
          </div>
          <NotificationsBell />
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">{children}</main>
        {user.role === "admin" && (
          <footer data-testid="admin-build-footer" className="border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400 lg:px-8">
            ARPS · frontend build {APP_BUILD}
          </footer>
        )}
      </div>
      {user?.is_demo && (
        <div data-testid="demo-watermark" className="pointer-events-none fixed left-1/2 top-2 z-40 -translate-x-1/2">
          <span className="rounded-full border border-amber-300 bg-amber-50/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 shadow-sm backdrop-blur">
            Demo — sample data
          </span>
        </div>
      )}
      <DemoTour />
    </div>
  );
}
